"use client"

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { collection, getDocs, setDoc, doc } from "firebase/firestore"
import { db } from "../firebase"
import {
  FaUsers,
  FaSpinner,
  FaExclamationTriangle,
  FaCrown,
  FaCalendarAlt,
  FaArrowRight,
  FaRedo,
  FaGraduationCap,
  FaHistory,
  FaClock,
} from "react-icons/fa"

const ECYearsPage = () => {
  const [availableYears, setAvailableYears] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [visibleSections, setVisibleSections] = useState(new Set())

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    fetchAvailableYears()
  }, [])

  const fetchAvailableYears = async () => {
    try {
      setLoading(true)
      setError(null)

      const executiveCommitteeRef = collection(db, "executiveCommittee")
      const snapshot = await getDocs(executiveCommitteeRef)

      if (snapshot.empty) {
        // If empty, create sample years
        const currentYear = new Date().getFullYear()
        const sampleYears = [
          `${currentYear}-${currentYear + 1}`,
          `${currentYear - 1}-${currentYear}`,
          `${currentYear - 2}-${currentYear - 1}`,
        ]

        for (const year of sampleYears) {
          try {
            // ✅ Create top-level year document
            await setDoc(doc(db, "executiveCommittee", year), {
              createdAt: new Date().toISOString(),
              placeholder: true,
            })

            // ✅ Create a placeholder in members subcollection
            const placeholderRef = doc(db, `executiveCommittee/${year}/members`, "placeholder")
            await setDoc(placeholderRef, {
              isPlaceholder: true,
              createdAt: new Date().toISOString(),
              note: `This is a placeholder for ${year}`,
              year: year,
            })
          } catch (createError) {
            console.error(`Error creating placeholder for ${year}:`, createError)
          }
        }

        // Retry fetching after creation
        await new Promise((res) => setTimeout(res, 1000)) // Give Firestore time
        const retrySnapshot = await getDocs(executiveCommitteeRef)

        if (!retrySnapshot.empty) {
          const years = retrySnapshot.docs
            .map((doc) => doc.id)
            .filter((id) => /^\d{4}-\d{4}$/.test(id))
            .sort()
            .reverse()
          setAvailableYears(years)
          return
        }

        // Fallback: display sample years manually
        setAvailableYears(sampleYears)
        return
      }

      const years = snapshot.docs
        .map((doc) => doc.id)
        .filter((id) => /^\d{4}-\d{4}$/.test(id))
        .sort()
        .reverse()

      setAvailableYears(years)
      if (years.length === 0) {
        setError("No Executive Committee data found.")
      }
    } catch (err) {
      console.error("Failed to fetch available years:", err)
      const fallbackYears = [
        `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
        `${new Date().getFullYear() - 1}-${new Date().getFullYear()}`,
        `${new Date().getFullYear() - 2}-${new Date().getFullYear() - 1}`,
      ]
      setAvailableYears(fallbackYears)
      setError(`Error loading data from Firebase: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const getYearType = (year) => {
    const current = new Date().getFullYear()
    const startYear = Number.parseInt(year.split("-")[0])
    if (startYear === current || startYear === current - 1) return "Current"
    if (startYear > current) return "Upcoming"
    return "Past"
  }

  const getYearColor = (type) => {
    switch (type) {
      case "Current":
        return "from-emerald-500 via-green-500 to-teal-600"
      case "Upcoming":
        return "from-blue-500 via-indigo-500 to-purple-600"
      case "Past":
      default:
        return "from-purple-500 via-pink-500 to-rose-600"
    }
  }

  const getYearIcon = (type) => {
    switch (type) {
      case "Current":
        return FaCrown
      case "Upcoming":
        return FaClock
      case "Past":
      default:
        return FaHistory
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
        <div className="text-center">
          <div className="relative">
            <FaSpinner className="text-blue-600 animate-spin w-16 h-16 mx-auto mb-6" />
            <div className="absolute inset-0 bg-blue-600/20 rounded-full animate-ping"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Loading Executive Committee</h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg">Fetching available years...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-teal-600 text-white py-20">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/80 via-purple-600/80 to-teal-600/80"></div>

        {/* Floating Background Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-float"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-white/10 rounded-full animate-float-delayed"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-float"></div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <FaGraduationCap className="w-16 h-16 mx-auto mb-6 text-white/90" />
          </div>
          <h1 className="text-4xl sm:text-6xl font-extrabold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
            Executive Committee
          </h1>
          <p className="text-xl sm:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Explore our leadership across different academic years and discover the dedicated individuals who have
            shaped our chapter.
          </p>
          <div className="flex items-center justify-center gap-2 text-blue-200">
            <FaCalendarAlt className="w-5 h-5" />
            <span className="text-lg">Select an academic year to view committee members</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {error && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-600 rounded-2xl p-6 mb-8 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FaExclamationTriangle className="text-yellow-600 dark:text-yellow-400 w-6 h-6 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">Notice</h3>
                  <p className="text-yellow-700 dark:text-yellow-300">{error}</p>
                </div>
              </div>
              <button
                onClick={fetchAvailableYears}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
              >
                <FaRedo className="w-4 h-4" />
                Retry
              </button>
            </div>
          </div>
        )}

        {availableYears.length === 0 ? (
          <div className="text-center py-20">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-12 max-w-md mx-auto">
              <FaUsers className="w-16 h-16 text-gray-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">No Data Available</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                No Executive Committee years found in the database.
              </p>
              <button
                onClick={fetchAvailableYears}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-lg flex items-center gap-2 mx-auto"
              >
                <FaRedo className="w-4 h-4" />
                Retry Fetch
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Years</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{availableYears.length}</p>
                  </div>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                    <FaCalendarAlt className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Year</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {availableYears.find((year) => getYearType(year) === "Current") || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                    <FaCrown className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Legacy Years</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {availableYears.filter((year) => getYearType(year) === "Past").length}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                    <FaHistory className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Years Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {availableYears.map((year, index) => {
                const type = getYearType(year)
                const gradient = getYearColor(type)
                const Icon = getYearIcon(type)

                return (
                  <Link
                    key={year}
                    to={`/ec/${year}`}
                    className="group relative overflow-hidden"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div
                      className={`relative p-8 rounded-3xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-500 bg-gradient-to-br ${gradient} text-white group-hover:rotate-1`}
                    >
                      {/* Background Pattern */}
                      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      {/* Content */}
                      <div className="relative z-10">
                        <div className="flex items-center justify-between mb-6">
                          <Icon className="w-8 h-8 text-white/90" />
                          <span className="text-sm bg-white/20 backdrop-blur-sm text-white rounded-full px-4 py-2 font-semibold border border-white/30">
                            {type}
                          </span>
                        </div>

                        <h3 className="text-3xl font-bold mb-3 group-hover:scale-110 transition-transform duration-300">
                          {year}
                        </h3>

                        <p className="text-white/90 mb-6 text-lg">Academic Year {year}</p>

                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/80">Click to view members</span>
                          <FaArrowRight className="w-5 h-5 text-white/80 group-hover:translate-x-2 transition-transform duration-300" />
                        </div>
                      </div>

                      {/* Hover Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* Call to Action */}
            
          </>
        )}
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}

export default ECYearsPage
